import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ClipboardCheck, Percent, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { FormField } from '@/components/forms/FormField'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { StatCard } from '@/components/ui/StatCard'
import { classesQuery } from '@/features/academic/api'
import { overviewQuery } from '@/features/attendance/api'
import { AttendanceRegister } from '@/features/attendance/components/AttendanceRegister'
import { formatLongDay, isValidIsoDate, todayIso } from '@/features/attendance/dates'
import { formatRate } from '@/features/attendance/status'
import type { OverviewSection } from '@/features/attendance/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function AttendanceRegisterPage() {
  const { t } = useTranslation('attendance')
  useDocumentTitle('Daily register')
  const [params, setParams] = useSearchParams()
  const today = todayIso()
  const rawDate = params.get('date')
  const date = isValidIsoDate(rawDate) ? rawDate : today
  const sectionId = Number(params.get('section') ?? 0)
  const classes = useQuery(classesQuery())
  const classOfSection = classes.data?.find((c) => c.sections.some((s) => s.id === sectionId))
  const classId = params.get('class') ?? (classOfSection ? String(classOfSection.id) : '')
  const sections = classes.data?.find((c) => String(c.id) === classId)?.sections ?? []

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: true })
  }

  return (
    <>
      <PageHeader
        title={t('Daily register')}
        description={t('Take or correct student attendance for a section and day.')}
        breadcrumbs={[{ label: t('Attendance'), to: '/admin/attendance' }, { label: t('Daily register') }]}
        homeHref="/admin"
        actions={
          sectionId > 0 ? (
            <Button asChild variant="secondary" leftIcon={<CalendarDays />}>
              <Link to={`/admin/attendance/sections/${sectionId}?month=${date.slice(0, 7)}`}>
                {t('Monthly view')}
              </Link>
            </Button>
          ) : null
        }
      />

      <Card className="mb-6">
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <FormField label={t('Class')}>
            <Select
              placeholder={t('All classes')}
              value={classId}
              onChange={(e) => set({ class: e.target.value, section: null })}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </FormField>
          <FormField label={t('Section')}>
            <Select
              placeholder={classId ? t('Choose a section') : t('Choose a class first')}
              disabled={!classId}
              value={sectionId > 0 ? sectionId : ''}
              onChange={(e) => set({ section: e.target.value })}
              options={sections.map((s) => ({
                value: s.id,
                label: `${s.name ? t('Section {{name}}', { name: s.name }) : t('Whole class')}${s.classTeacher ? ` · ${s.classTeacher.name}` : ''}`,
              }))}
            />
          </FormField>
          <FormField label={t('Date')} hint={date === today ? t('Today') : formatLongDay(date)}>
            <Input
              type="date"
              max={today}
              value={date}
              onChange={(e) => set({ date: e.target.value || null })}
            />
          </FormField>
        </CardBody>
      </Card>

      {sectionId > 0 ? (
        <AttendanceRegister sectionId={sectionId} date={date} />
      ) : (
        <SchoolOverview
          date={date}
          onOpen={(s) => set({ class: String(s.classId), section: String(s.id) })}
        />
      )}
    </>
  )
}

function SchoolOverview({ date, onOpen }: { date: string; onOpen: (s: OverviewSection) => void }) {
  const { t } = useTranslation('attendance')
  const overview = useQuery(overviewQuery(date))
  const data = overview.data
  const sections = data?.sections ?? []
  const marked = sections.filter((s) => s.summary.marked > 0).length
  const enrolled = sections.reduce((n, s) => n + s.enrolled, 0)

  const columns: Column<OverviewSection>[] = [
    {
      key: 'section',
      header: t('Section'),
      cell: (s) => (
        <div>
          <p className="font-medium text-ink">{s.name}</p>
          <p className="text-xs text-muted">{s.classTeacher ?? t('No class teacher')}</p>
        </div>
      ),
    },
    { key: 'progress', header: t('Marked'), cell: (s) => <MarkedBadge s={s} /> },
    {
      key: 'absent',
      header: t('Absent'),
      hideBelow: 'md',
      align: 'right',
      cell: (s) => <span className="tabular">{s.summary.absent}</span>,
    },
    {
      key: 'late',
      header: t('Late'),
      hideBelow: 'lg',
      align: 'right',
      cell: (s) => <span className="tabular">{s.summary.late}</span>,
    },
    {
      key: 'rate',
      header: t('Rate'),
      align: 'right',
      cell: (s) => <span className="font-semibold text-ink tabular">{formatRate(s.summary.rate)}</span>,
    },
    {
      key: 'open',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: (s) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onOpen(s)}
          aria-label={t('Open register of {{section}}', { section: s.name })}
        >
          {t('Open')}
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {data && !data.workingDay.isWorkingDay ? (
        <Alert
          tone="info"
          title={
            data.workingDay.holiday
              ? t('Holiday — {{name}}', { name: data.workingDay.holiday.name })
              : t('The school is closed on this day')
          }
        >
          {t('No attendance is taken on {{day}}.', { day: formatLongDay(date) })}
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label={t('Sections marked')}
          value={data ? `${marked}/${sections.length}` : '—'}
          icon={<ClipboardCheck />}
          loading={overview.isPending}
          hint={formatLongDay(date)}
        />
        <StatCard
          label={t('Students marked')}
          value={data ? `${data.summary.marked}/${enrolled}` : '—'}
          icon={<UsersRound />}
          tone="info"
          loading={overview.isPending}
          hint={
            data
              ? t('{{absent}} absent · {{late}} late', {
                  absent: data.summary.absent,
                  late: data.summary.late,
                })
              : undefined
          }
        />
        <StatCard
          label={t('Present rate')}
          value={formatRate(data?.summary.rate)}
          icon={<Percent />}
          tone="success"
          loading={overview.isPending}
          hint={t('Present + late + ½ half days, excused excluded')}
        />
      </div>
      <Card className="overflow-hidden">
        <CardHeader title={t('Sections')} description={t('Choose a section to open its register.')} />
        <DataTable
          caption={t('Attendance progress on {{date}}', { date: formatLongDay(date) })}
          columns={columns}
          rows={data?.sections}
          getRowId={(s) => s.id}
          loading={overview.isPending}
          error={overview.error}
          onRetry={() => void overview.refetch()}
          mobileCard={(s) => (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{s.name}</p>
                <p className="text-xs text-muted">
                  {s.classTeacher ?? t('No class teacher')} · {formatRate(s.summary.rate)}
                </p>
              </div>
              <MarkedBadge s={s} />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onOpen(s)}
                aria-label={t('Open register of {{section}}', { section: s.name })}
              >
                {t('Open')}
              </Button>
            </div>
          )}
        />
      </Card>
    </div>
  )
}

function MarkedBadge({ s }: { s: OverviewSection }) {
  const { t } = useTranslation('attendance')
  const done = s.enrolled > 0 && s.summary.marked >= s.enrolled
  const tone = done ? 'success' : s.summary.marked > 0 ? 'warning' : 'neutral'
  return (
    <Badge tone={tone} dot>
      <span className="tabular">
        {s.summary.marked}/{s.enrolled}
      </span>
      <span className="sr-only">{t('students marked')}</span>
    </Badge>
  )
}
